import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { MARK_ORDER_READY_FOR_PICKUP_SUBSCRIPTION } from 'api/graphql/orders';

const TIMEOUT_MS = 30000;

/**
 * Marks an order item ready for pickup. This is a SUBSCRIPTION (delivery is
 * async): it resolves to `{ ok, message }` when the item reaches a READY status
 * or an error/timeout occurs. Resolves at most once per call.
 */
export function useMarkOrderReady() {
  const client = useApolloClient();
  const active = useRef(new Map());

  // Tear down any in-flight subscriptions on unmount.
  useEffect(
    () => () => {
      active.current.forEach((entry) => {
        try {
          entry.sub.unsubscribe();
        } catch {
          /* noop */
        }
        clearTimeout(entry.timer);
      });
      active.current.clear();
    },
    [],
  );

  const markReady = useCallback(
    (orderItemId) =>
      new Promise((resolve) => {
        const cleanup = () => {
          const entry = active.current.get(orderItemId);
          if (!entry) return;
          try {
            entry.sub.unsubscribe();
          } catch {
            /* noop */
          }
          clearTimeout(entry.timer);
          active.current.delete(orderItemId);
        };

        const observable = client.subscribe({
          query: MARK_ORDER_READY_FOR_PICKUP_SUBSCRIPTION,
          variables: { orderItemId },
        });

        const sub = observable.subscribe({
          next({ data }) {
            const result = data?.markOrderReadyForPickup;
            if (!result) return;

            if (!result.success && result.error) {
              cleanup();
              resolve({ ok: false, message: result.error.message });
              return;
            }

            const status = String(result.orderItemStatus || '').toUpperCase();
            if (result.success && (status === 'READY_FOR_PICKUP' || status === 'READY_FOR_DELIVERY')) {
              cleanup();
              resolve({ ok: true, message: result.message });
            }
            // Otherwise it's an intermediate ack — keep waiting.
          },
          error(err) {
            cleanup();
            resolve({ ok: false, message: err?.message });
          },
        });

        const timer = setTimeout(() => {
          cleanup();
          resolve({ ok: false, message: 'Timed out. Please try again.' });
        }, TIMEOUT_MS);

        active.current.set(orderItemId, { sub, timer });
      }),
    [client],
  );

  return { markReady };
}
